import {
  AttributeDialodData,
  AttributeDialogComponent
} from '@/components/attribute-dialog/attribute-dialog.component';
import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { DictionaryHelper } from '@/helpers/dictionary.helper';
import { PropertyHelper } from '@/helpers/property.helper';
import { Paging } from '@/models/Paging';
import { AlertService } from '@/services/alert.service';
import { DictionaryCacheService } from '@/services/dictionary-cache.service';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Attribute, DataDictionaryService } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { CommaStringArrayPipe } from '../../../extensions/commaStringArrayPipe';
import { BooleanFormatPipe } from '../../../extensions/booleanPipe';
import { MatMenuModule } from '@angular/material/menu';
import { NgFor } from '@angular/common';

import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
    selector: 'app-admin-attributes',
    templateUrl: './admin-attributes.component.html',
    styleUrls: ['./admin-attributes.component.scss'],
    standalone: true,
    imports: [MatFormFieldModule, MatIconModule, MatInputModule, MatSlideToggleModule, MatButtonModule, MatTableModule, MatSortModule, NgFor, MatMenuModule, MatPaginatorModule, BooleanFormatPipe, CommaStringArrayPipe]
})
export class AdminAttributesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: MatTableDataSource<Attribute>;
  data: Attribute[];
  displayFullColumns = false;
  tableClass = 'light';
  lightColumns = [
    'code',
    'category',
    'groupCode',
    'summary',
    'availableValues',
    'display.name',
    'display.order',
    'actions'
  ];
  fullColumns = [
    'code',
    'type',
    'category',
    'groupCode',
    'required',
    'summary',
    'unit',
    'inCertification',
    'rescue',
    'allowedPropertyTypes',
    'availableValues',
    'display.name',
    'display.order',
    'display.description',
    'actions'
  ];
  displayedColumns = [];
  resultsLength = 0;
  isLoadingResults = true;

  sub = new Subscription();
  sortSub = new Subscription();
  deleteSub = new Subscription();

  paging = new Paging();

  constructor(
    private dictService: DataDictionaryService,
    private dictCacheService: DictionaryCacheService,
    private dialog: MatDialog,
    private alertService: AlertService,
    public propHelper: PropertyHelper,
    public dictHelper: DictionaryHelper,
    private changeDetectorRefs: ChangeDetectorRef
  ) {
    this.displayedColumns = this.lightColumns;
  }

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.sortSub.unsubscribe();
    this.deleteSub.unsubscribe();
  }

  loadData() {
    let filter: string;
    if (this.dataSource && this.dataSource.filter) {
      filter = this.dataSource.filter;
    }
    this.dictService.getDictionaryAttributes().subscribe((data) => {
      const displayedData = [];
      data.forEach((i) => {
        const row = {
          attr: i,
          code: i.code,
          categoory: i.category,
          groupCode: i.groupCode,
          type: this.dictHelper.getAttributeTypeName(i.type),
          allowedPropertyTypes:
            i.allowedPropertyTypes?.length > 0
              ? i.allowedPropertyTypes.map((t) => this.propHelper.getTypeName(t))
              : ['Tous']
        };
        displayedData.push(row);
      });
      this.dataSource = new MatTableDataSource(displayedData);
      this.dataSource.paginator = this.paginator;
      this.dataSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case 'display.name':
            return item.display.name;
          case 'display.order':
            return item.display.order;
          case 'display.description':
            return item.display.description;
          default:
            return item[property];
        }
      };
      this.dataSource.sort = this.sort;

      if (filter) {
        this.applyFilter(filter);
      }
    });
    this.dictCacheService.checkDictionaryVersion(true);
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sortSub = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
      this.tableClass = 'light';
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
      this.tableClass = 'full';
    }
    this.changeDetectorRefs.detectChanges();
  }

  add() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(AttributeDialogComponent, {
        // width: '20rem'
        data: new AttributeDialodData()
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  edit(attrToEdit: Attribute) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(AttributeDialogComponent, {
        // width: '20rem'
        data: { attr: attrToEdit, edition: true }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmDelete(attr: Attribute) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression de l\'attribut "' + attr.code + '" ?',
          details: 'Tous les attributs du groupe seront supprimés, ainsi que leurs valeurs définis dans les biens !'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(attr);
        }
      });
    }
  }

  doDelete(attr: Attribute) {
    this.deleteSub = this.dictService.deleteDictionaryAttribute(attr.code).subscribe((result) => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
    });
  }
}
